// src/pages/courtowner/BlockSlotsBulk.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CourtOwnerSidebar from "../../components/courtowner/CourtOwnerSidebar";

const API_BASE = "https://api.getfitindia.in/api";

const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const WEEK_DAYS = [
  { index: 0, label: "Sunday" },
  { index: 1, label: "Monday" },
  { index: 2, label: "Tuesday" },
  { index: 3, label: "Wednesday" },
  { index: 4, label: "Thursday" },
  { index: 5, label: "Friday" },
  { index: 6, label: "Saturday" },
];

/* --------------------- Time Dropdown --------------------- */
const TimeDropdown = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <div
        className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm flex items-center cursor-pointer bg-white"
        onClick={() => setOpen(!open)}
      >
        {value ? formatTime(value) : placeholder}
      </div>

      {open && (
        <div className="absolute mt-1 w-full bg-white shadow-lg rounded-lg border max-h-40 overflow-y-auto z-50">
          {options.map((t) => (
            <div
              key={t}
              className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
            >
              {formatTime(t)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function BlockSlotsBulk() {
  const navigate = useNavigate();

  /* --------------------- State --------------------- */
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState("");

  const [availableSlots, setAvailableSlots] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reason, setReason] = useState("");

  const [slotsToBlock, setSlotsToBlock] = useState([]);

  /* ---------------------- Repeat Booking States ---------------------- */
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [repeatEvery, setRepeatEvery] = useState(1); // every N weeks
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [repeatEndDate, setRepeatEndDate] = useState("");
  const [repeatDates, setRepeatDates] = useState([]);
  const [repeatSummary, setRepeatSummary] = useState("");
const [blockedSlots, setBlockedSlots] = useState([]);

  const getAuthHeader = () => {
    const token =
      localStorage.getItem("courtOwnerToken") || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  /* --------------------- Load Venues --------------------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/courtowner/venues`, {
          headers: { ...getAuthHeader() },
        });
        const data = await res.json();
        setVenues(Array.isArray(data) ? data : []);
      } catch {
        alert("Failed to load venues");
      }
    })();
  }, []);

  /* --------------------- Load Courts --------------------- */
  useEffect(() => {
    if (!selectedVenue) {
      setCourts([]);
      setSelectedCourt("");
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/slots/${selectedVenue}/courts`, {
          headers: { ...getAuthHeader() },
        });
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.courts || [];
        setCourts(list);
      } catch {
        alert("Failed to load courts");
      }
    })();
  }, [selectedVenue]);

  /* --------------------- Load Slots --------------------- */
  useEffect(() => {
    if (!selectedVenue || !selectedCourt) {
      setAvailableSlots([]);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/slots/${selectedVenue}/courts`, {
          headers: { ...getAuthHeader() },
        });
        const data = await res.json();
        const court = Array.isArray(data)
          ? data.find((c) => c._id === selectedCourt)
          : data.courts?.find((c) => c._id === selectedCourt);
        setAvailableSlots(court?.slots || []);
      } catch {
        alert("Failed to load slots");
      }
    })();
  }, [selectedCourt, selectedVenue]);

  const selectedCourtObj = courts.find((c) => c._id === selectedCourt) || {};
  const selectedCourtName =
    selectedCourtObj.courtName || selectedCourtObj.name || "Selected Court";

  const startTimeOptions = useMemo(
    () => Array.from(new Set(availableSlots.map((s) => s.startTime))).sort(),
    [availableSlots]
  );

  /* Auto-update endTime when startTime changes */
  useEffect(() => {
    if (!startTime) {
      setEndTime("");
      return;
    }
    const slot = availableSlots.find((s) => s.startTime === startTime);
    setEndTime(slot?.endTime || "");
  }, [startTime, availableSlots]);

  const canAddSlot =
    selectedVenue &&
    selectedCourt &&
    startDate &&
    endDate &&
    startTime &&
    endTime &&
    availableSlots.length;

  const totalAmount = slotsToBlock.reduce(
    (sum, s) => sum + Number(s.price || 0),
    0
  );

  /* --------------------- Repeat Helpers --------------------- */

  const toggleWeekday = (label) => {
    setSelectedWeekdays((prev) =>
      prev.includes(label)
        ? prev.filter((d) => d !== label)
        : [...prev, label]
    );
  };

  // Generate repeatDates based on:
  // - startDate
  // - repeatEndDate
  // - repeatEvery N weeks
  // - selectedWeekdays (Sunday, Monday, etc.)
const computeRepeatDates = () => {
  if (!startDate || !repeatEndDate || !selectedWeekdays.length) return [];

  const start = new Date(startDate);
  const end = new Date(repeatEndDate);
  const weekdayIndexes = selectedWeekdays.map(
    (label) => WEEK_DAYS.find((d) => d.label === label).index
  );

  const out = [];

  // Find first matching weekday after start date
  const firstWeekMatches = [];
  for (let i = 0; i < 7; i++) {
    const next = new Date(start);
    next.setDate(start.getDate() + i);
    if (weekdayIndexes.includes(next.getDay())) {
      firstWeekMatches.push(next.toISOString().split("T")[0]);
    }
  }
  if (!firstWeekMatches.length) return [];

  // Get first matching date
  const firstMatch = new Date(firstWeekMatches[0]);

  // Loop through cycles until end date
  let cycleStart = new Date(firstMatch);
  while (cycleStart <= end) {
    // In each cycle, loop every day for 7 days × repeatEvery
    for (let d = 0; d < repeatEvery * 7; d++) {
      const match = new Date(cycleStart);
      match.setDate(cycleStart.getDate() + d);

      if (
        match >= start &&
        match <= end &&
        weekdayIndexes.includes(match.getDay())
      ) {
        out.push(match.toISOString().split("T")[0]);
      }
    }

    // Move to next cycle
    cycleStart.setDate(cycleStart.getDate() + repeatEvery * 7);
  }

  return [...new Set(out)].sort();
};




const handleConfirmRepeat = () => {
  setTimeout(() => {
    const dates = computeRepeatDates();
    if (!dates.length) return alert("No dates generated.");

    setRepeatDates(dates);

    const shortDays = selectedWeekdays.map(d => d.slice(0,3)).join(", ");
    setRepeatSummary(
      `Repeats every ${repeatEvery} week(s) on ${shortDays} until ${formatDate(repeatEndDate)} (${dates.length} days)`
    );

    setRepeatOpen(false);
  }, 0);
};


  const clearRepeat = () => {
    setRepeatOpen(false);
    setRepeatEvery(1);
    setRepeatDates([]);
    setRepeatEndDate("");
    setSelectedWeekdays([]);
    setRepeatSummary("");
  };



  useEffect(() => {
  if (!startDate || !repeatEndDate || !selectedWeekdays.length) {
    setRepeatDates([]);
    return;
  }

  const dates = computeRepeatDates();
  setRepeatDates(dates);

  setRepeatSummary(
    `Repeats every ${repeatEvery} week(s) on ${selectedWeekdays.join(", ")} until ${formatDate(repeatEndDate)} (${dates.length} days)`
  );
}, [startDate, repeatEndDate, selectedWeekdays, repeatEvery]);


const loadBlockedSlots = async () => {
  if (!selectedCourt) return;

  let params = new URLSearchParams();
  if (repeatDates.length > 0) {
    params.set("dates", JSON.stringify(repeatDates)); // repeat mode
  } else if (startDate && endDate) {
    params.set("start", startDate);
    params.set("end", endDate);
  } else if (startDate) {
    params.set("date", startDate);
  }

  try {
    const res = await fetch(
      `${API_BASE}/courtowner/blockslots/${selectedCourt}?${params.toString()}`,
      {
        headers: { ...getAuthHeader() },
      }
    );

    if (!res.ok) throw new Error("Failed to fetch blocked slots");
    
    const data = await res.json();
    setBlockedSlots(Array.isArray(data) ? data : []);
  } catch (err) {
    console.warn("⚠ Failed to load blocked slots:", err);
  }
};


useEffect(() => {
  loadBlockedSlots();
}, [selectedCourt, startDate, endDate, repeatDates]);
const freeSlots = useMemo(() => {
  if (!blockedSlots.length) return availableSlots;

  return availableSlots.filter(
    (slot) =>
      !blockedSlots.some(
        (blocked) =>
          blocked.startTime === slot.startTime &&
          blocked.endTime === slot.endTime
      )
  );
}, [availableSlots, blockedSlots]);

  /* --------------------- Add Slot --------------------- */

  const handleAddSlot = () => {
    if (!canAddSlot) return;

    const template = availableSlots.find(
      (s) => s.startTime === startTime && s.endTime === endTime
    );
    if (!template) return alert("Slot not found in ManageSlots.");

    const exists = slotsToBlock.some(
      (s) => s.startTime === startTime && s.endTime === endTime
    );
    if (exists) return alert("Slot already added.");

    const newSlot = {
      startTime: template.startTime,
      endTime: template.endTime,
      price: template.price || 0,
    };

    setSlotsToBlock((prev) =>
      [...prev, newSlot].sort((a, b) => a.startTime.localeCompare(b.startTime))
    );
  };

  const removeSlotFromList = (i) => {
    setSlotsToBlock((prev) => prev.filter((_, idx) => idx !== i));
  };

  /* --------------------- Submit --------------------- */

  const handleBlockSubmit = async (e) => {
    e.preventDefault();

    if (!slotsToBlock.length) {
      alert("Add at least one slot.");
      return;
    }

    // Repeat is enabled ONLY if we have generated dates
    const repeat =
      repeatDates.length > 0
        ? {
            enabled: true,
            every: repeatEvery,
            unit: "week",
            days: selectedWeekdays,
            endOn: repeatEndDate,
            dates: repeatDates,
          }
        : { enabled: false };

    // With "Option B" we allow submit anytime.
    // If repeat.enabled === true → we send ONLY repeat and NOT start/end date.
    // If repeat.enabled === false → we send startDate + endDate range.
    const body = {
      venueId: selectedVenue,
      courtId: selectedCourt,
      reason,
      slots: slotsToBlock.map((s) => ({
        startTime: s.startTime,
        endTime: s.endTime,
        price: s.price,
      })),
      ...(repeat.enabled ? { repeat } : { startDate, endDate }),
    };

    console.log("🛰️ Sending body to API:", body);

    try {
      const res = await fetch(`${API_BASE}/courtowner/blockslots`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to block slots");
      }

      alert("Bulk slots blocked successfully!");
      setSlotsToBlock([]);
      clearRepeat();
    } catch (err) {
      alert(err.message);
    }
  };

  /* --------------------- UI --------------------- */

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:block w-64 bg-white shadow">
        <CourtOwnerSidebar />
      </aside>

      {/* Main */}
      <main className="flex-1 lg:pl-0">
        {/* Top bar */}
        


 <div className="bg-[#0f2250] text-white sticky top-0 z-20">
          <div className="max-w-6xl  px-4 py-4 flex items-center gap-2">
            {/* <button
              onClick={() => navigate(-1)}
              className="p-1 rounded hover:bg-white/10"
              aria-label="Back"
            >
              <ChevronLeft />
            </button> */}
            <div className="lg:hidden mt-10">
                          <CourtOwnerSidebar />
                        </div>
            <h1 className="text-lg font-semibold  sm:ms-0 lg:ms-0 mt-1 ">Block Slots (Bulk)</h1>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleBlockSubmit}
          className="max-w-6xl mx-auto px-4 pb-32 bg-white mt-4 rounded-2xl p-4 md:p-6"
        >
          {/* Venue + Court */}
          <div className="grid md:grid-cols-2 gap-3">
            {/* Venue */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Property
              </label>
              <select
                value={selectedVenue}
                onChange={(e) => {
                  setSelectedVenue(e.target.value);
                  setSelectedCourt("");
                  setSlotsToBlock([]);
                }}
                className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm"
              >
                <option value="">Select Property</option>
                {venues.map((v) => (
                  <option key={v._id} value={v._id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Court */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Court
              </label>
              <select
                value={selectedCourt}
                onChange={(e) => {
                  setSelectedCourt(e.target.value);
                  setSlotsToBlock([]);
                }}
                className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm"
              >
                <option value="">Select Court</option>
                {courts.map((c, i) => (
                  <option key={c._id || i} value={c._id}>
                    {c.courtName || c.name || `Court ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="w-full h-9 rounded-lg border px-3 text-sm"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setSlotsToBlock([]);
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                className="w-full h-9 rounded-lg border px-3 text-sm"
                min={startDate || undefined}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setSlotsToBlock([]);
                }}
              />
            </div>
          </div>

          {/* Repeat Booking */}
          <div className="mt-5 rounded-2xl border border-gray-300 overflow-hidden">
            <div className="bg-[#0f2250] text-white px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">⟳</span>
                <span className="text-sm font-medium">Repeat Booking</span>
              </div>
              <button
                type="button"
                onClick={() => setRepeatOpen((p) => !p)}
                className="text-sm font-medium"
              >
                {repeatOpen ? "CLOSE" : "OPEN"}
              </button>
            </div>

            {repeatOpen && (
              <div className="bg-white px-4 py-4 space-y-4">
                {/* Repeat Every */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Repeat Every
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setRepeatEvery((n) => (n > 1 ? n - 1 : 1))
                      }
                      className="w-8 h-8 rounded-full border flex items-center justify-center text-lg"
                    >
                      –
                    </button>

                    <span className="text-sm font-semibold w-4 text-center">
                      {repeatEvery}
                    </span>

                    <button
                      type="button"
                      onClick={() => setRepeatEvery((n) => n + 1)}
                      className="w-8 h-8 rounded-full border flex items-center justify-center text-lg"
                    >
                      +
                    </button>

                    <span className="text-sm text-gray-700">Week</span>
                  </div>
                </div>

                {/* Weekdays */}
                <div>
                  <div className="text-xs font-semibold text-gray-700 mb-2">
                    REPEAT ON
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-sm">
                    {WEEK_DAYS.map((d) => {
                      const active = selectedWeekdays.includes(d.label);
                      return (
                        <button
                          key={d.label}
                          type="button"
                          onClick={() => toggleWeekday(d.label)}
                          className={`border rounded-lg py-1 px-2 ${
                            active
                              ? "bg-[#0f2250] text-white border-[#0f2250]"
                              : "bg-white text-gray-800 border-gray-300"
                          }`}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* END ON */}
                <div>
                  <div className="text-xs font-semibold text-gray-700 mb-2">
                    ENDS ON
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full border flex items-center justify-center border-gray-500">
                        <span className="w-2 h-2 rounded-full bg-[#0f2250]" />
                      </span>
                      <span className="text-sm">On</span>
                    </div>

                    <input
                      type="date"
                      className="h-9 border rounded-lg px-3 text-sm"
                      value={repeatEndDate}
                      min={startDate || undefined}
                      onChange={(e) => setRepeatEndDate(e.target.value)}
                    />
                  </div>
                </div>

                {/* Confirm */}
                <button
                  type="button"
                  onClick={handleConfirmRepeat}
                  disabled={
                    !startDate || !repeatEndDate || !selectedWeekdays.length
                  }
                  className={`w-full h-10 rounded-xl text-sm font-semibold ${
                    startDate && repeatEndDate && selectedWeekdays.length
                      ? "bg-[#0f2250] text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  CONFIRM
                </button>
              </div>
            )}

            {/* Summary */}
            {!repeatOpen && repeatSummary && (
              <div className="bg-white px-4 py-3 border-t text-xs text-gray-600">
                {repeatSummary}
              </div>
            )}
          </div>

          {/* Time */}
          {/* --------------------- Time ---------------------  */}
<div className="grid grid-cols-2 gap-4 mt-4">
  <div>
    <label className="block text-sm font-medium mb-1">
      Start Time
    </label>
    <TimeDropdown
      value={startTime}
      onChange={(v) => setStartTime(v)}
      options={[...new Set(freeSlots.map((s) => s.startTime))]}
      placeholder="Start Time"
    />
  </div>

  <div>
    <label className="block text-sm font-medium mb-1">
      End Time
    </label>
    <TimeDropdown
      value={endTime}
      onChange={(v) => setEndTime(v)}
   options={freeSlots
  .filter((s) => s.startTime >= startTime)
  .map((s) => s.endTime)}

      placeholder="End Time"
    />
  </div>
</div>


          {/* Reason */}
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-9 rounded-lg border px-3 text-sm"
              placeholder="Maintenance, event, etc."
            />
          </div>

          {/* Add slot */}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleAddSlot}
              disabled={!canAddSlot}
              className={`h-9 px-6 rounded-lg text-sm font-semibold ${
                canAddSlot
                  ? "bg-[#0f2250] text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              ADD SLOT
            </button>
          </div>

          {/* Slot Details */}
          {slotsToBlock.length > 0 && (
            <div className="mt-6 rounded-2xl bg-gray-200 p-4">
              <h3 className="text-base font-semibold text-gray-800">
                Slot Details
              </h3>

              <p className="text-sm text-gray-600 mt-1">
                {startDate ? `${formatDate(startDate)} → ${formatDate(endDate)}` : ""}
              </p>

              <div className="mt-3 space-y-3">
                {slotsToBlock.map((s, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl shadow-sm px-4 py-3 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm text-gray-600">
                        {selectedCourtName} | Full Court
                      </p>
                      <p className="text-sm font-semibold tracking-wide">
                        {formatTime(s.startTime)} - {formatTime(s.endTime)}
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-base font-medium">
                        ₹{Number(s.price || 0)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSlotFromList(idx)}
                        className="text-gray-500 hover:text-red-600 text-xl"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}

                <p className="text-sm text-gray-600">
                  Total: <span className="font-semibold">₹{totalAmount}</span>
                </p>
              </div>
            </div>
          )}

          {/* Bottom Fixed Bar */}
          <div
            className="
              fixed bottom-0 left-0 right-0
              bg-white border-t border-gray-300 px-4 py-3 
              lg:left-64
            "
          >
            <div className="max-w-5xl mx-auto flex gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-[1] h-9 rounded-xl border border-[#0f2250] text-[#0f2250] font-semibold"
              >
                CANCEL
              </button>

              <button
                type="submit"
                disabled={!slotsToBlock.length || !selectedVenue || !selectedCourt}
                className={`flex-[2] h-9 rounded-xl font-semibold ${
                  slotsToBlock.length && selectedVenue && selectedCourt
                    ? "bg-[#0f2250] text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                BLOCK SLOT
              </button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

export default BlockSlotsBulk;
